import {Loader} from 'components';
import React, {useState, useEffect, useCallback} from 'react';
import ReactApexChart from 'react-apexcharts';
import {Period} from 'routes/FraudDetection/Reconciliation/components/Period';

const FlaggedVsUnflaggedChart = ({data, loading, period, onPeriodChange}) => {
  const [chartData, setChartData] = useState({
    categories: [],
    flaggedData: [],
    unflaggedData: [],
  });

  const processChartData = useCallback(
    rawData => {
      if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
        return;
      }

      const groupedData = {};

      rawData.forEach(item => {
        const date = new Date(item.transaction_time);
        const formattedDate = formatDate(date, period);

        if (!groupedData[formattedDate]) {
          groupedData[formattedDate] = {
            flagged: 0,
            unflagged: 0,
          };
        }

        if (item.outcome === 'ALLOW') {
          groupedData[formattedDate].unflagged += Number.parseFloat(
            item.amount,
          );
        } else if (item.outcome === 'PENDING' || item.outcome === 'BLOCK') {
          groupedData[formattedDate].flagged += Number.parseFloat(item.amount);
        }
      });

      const sortedDates = Object.keys(groupedData).sort(
        (a, b) => new Date(a) - new Date(b),
      );

      const categories = sortedDates;
      const flaggedData = sortedDates.map(date => groupedData[date].flagged);
      const unflaggedData = sortedDates.map(
        date => groupedData[date].unflagged,
      );

      setChartData({
        categories,
        flaggedData,
        unflaggedData,
      });
    },
    [period],
  );

  useEffect(() => {
    if (data) {
      processChartData(data);
    }
  }, [data, processChartData]);

  const formatDate = (date, periodType) => {
    switch (periodType) {
      case 'day':
        return date.toISOString().split('T')[0];
      case 'week': {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        return weekStart.toISOString().split('T')[0];
      }
      case 'month':
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          '0',
        )}`;
      case 'year':
        return date.getFullYear().toString();
      default:
        return date.toISOString().split('T')[0];
    }
  };

  const chartOptions = {
    chart: {
      height: 350,
      type: 'line',
      toolbar: {
        show: false,
      },
      fontFamily: 'Inter, sans-serif',
    },
    colors: ['#3F7CDB', '#17A6A0'],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
      curve: 'straight',
    },
    markers: {
      size: 6,
      strokeWidth: 2,
      hover: {
        size: 8,
      },
      colors: ['#0B1C56', '#17A6A0'],
      strokeColors: ['#3F7CDB', '#17A6A0'],
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          colors: '#868C98',
          fontSize: '12px',
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: '#868C98',
          fontSize: '12px',
        },
        formatter: value => value.toLocaleString(),
      },
      min: 0,
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    tooltip: {
      custom: ({series, seriesIndex, dataPointIndex}) => {
        const value = series[seriesIndex][dataPointIndex];
        const formattedValue = value.toLocaleString();

        return `<div class="custom-tooltip" style="background: white; border-radius: 16px; outline: none; padding: 8px 16px; display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 20px; font-weight: 700; color: #000;">${formattedValue}</span>
        </div>`;
      },
      style: {
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
      },
      fixed: {
        enabled: false,
        position: 'topRight',
        offsetX: 0,
        offsetY: 0,
      },
    },
    legend: {
      show: false,
    },
  };

  const series = [
    {
      name: 'Flagged',
      data: chartData.flaggedData,
    },
    {
      name: 'Unflagged',
      data: chartData.unflaggedData,
    },
  ];

  return (
    <div className="bg-white shadow-cards w-full h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
      <div className="bg-[#F9F9F9] flex items-center justify-between py-3 px-6 rounded-t-xl">
        <div className="flex items-center gap-5">
          <p className="font-medium text-sm xl:text-base text-deepBlue">
            Flagged vs Unflagged
          </p>
          <div className="flex items-center gap-4">
            <div className="uppercase text-[10px] font-medium text-body flex items-center gap-1">
              <div className="bg-[#3F7CDB] rounded min-w-[14px] min-h-[14px]" />
              <p>flagged</p>
            </div>
            <div className="uppercase text-[10px] font-medium text-body flex items-center gap-1">
              <div className="bg-[#17A6A0] rounded min-w-[14px] min-h-[14px]" />
              <p>Unflagged</p>
            </div>
          </div>
        </div>

        <Period className="h-7" date={period} setDate={onPeriodChange} />
      </div>

      {loading ? (
        <Loader height={30} />
      ) : (
        <ReactApexChart
          options={chartOptions}
          series={series}
          type="line"
          height={350}
        />
      )}
    </div>
  );
};

export default FlaggedVsUnflaggedChart;
